import { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { syncOrders } from "../slices/orders/thunk";
import { RootState, AppDispatch } from "../store";
import realOrderService from "../helpers/real_order_helper";
import useLocalStorageSettings from "./useLocalStorageSettings";
import { useNotifications } from "./useNotifications";

export interface SyncState {
  isActive: boolean;
  isSyncing: boolean;
  lastSyncAt: string | null;
  pendingCount: number;
  syncError: string | null;
  nextSyncAt: string | null;
}

export const useSyncWorker = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { settings } = useLocalStorageSettings();
  const { addSuccess, addError, addWarning } = useNotifications();
  const orders = useSelector((state: RootState) => state.Orders.orders);

  const [syncState, setSyncState] = useState<SyncState>({
    isActive: false,
    isSyncing: false,
    lastSyncAt: null,
    pendingCount: 0,
    syncError: null,
    nextSyncAt: null,
  });

  const workerRef = useRef<Worker | null>(null);

  // Calcular pedidos pendentes
  useEffect(() => {
    const pending = orders.filter((order: { status: number }) => order.status !== 2).length;
    setSyncState((prev) => ({ ...prev, pendingCount: pending }));
  }, [orders]);

  // Inicializar worker
  useEffect(() => {
    if (!workerRef.current) {
      try {
        workerRef.current = new Worker(`${process.env.PUBLIC_URL}/sync.worker.js`);

        // Listener de mensagens do worker
        workerRef.current.onmessage = (event) => {
          handleWorkerMessage(event.data);
        };

        workerRef.current.onerror = (error) => {
          console.error("❌ Erro no worker:", error);
          setSyncState((prev) => ({
            ...prev,
            syncError: "Erro no worker de sincronização",
            isSyncing: false,
          }));
        };

        console.log("✅ Sync Worker inicializado");
      } catch (error) {
        console.error("❌ Erro ao criar worker:", error);
      }
    }

    // Cleanup
    return () => {
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
      }
    };
  }, []);

  // Tratar mensagens do worker
  const handleWorkerMessage = useCallback(
    async (data: { type: string; payload?: unknown }) => {
      const { type, payload } = data;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const p = payload as any;

      switch (type) {
        case "SYNC_STARTED":
          setSyncState((prev) => ({
            ...prev,
            isActive: true,
            nextSyncAt: p?.nextSyncAt || null,
          }));
          console.log("🔄 Sincronização automática iniciada");
          break;

        case "SYNC_STOPPED":
          setSyncState((prev) => ({
            ...prev,
            isActive: false,
            nextSyncAt: null,
          }));
          console.log("⏸️ Sincronização automática parada");
          break;

        case "SYNC_START":
          setSyncState((prev) => ({ ...prev, isSyncing: true, syncError: null }));
          break;

        case "SYNC_PROGRESS":
          console.log(`📊 Progresso: ${p?.current}/${p?.total}`);
          break;

        case "SYNC_SUCCESS":
          setSyncState((prev) => ({
            ...prev,
            isSyncing: false,
            lastSyncAt: p?.completedAt || new Date().toISOString(),
            syncError: null,
          }));

          if (p?.syncedCount > 0) {
            addSuccess(`✅ ${p.syncedCount} pedidos sincronizados com sucesso`);
          }

          // Atualizar pedidos no Redux
          await dispatch(syncOrders());
          break;

        case "SYNC_ERROR":
          setSyncState((prev) => ({
            ...prev,
            isSyncing: false,
            syncError: p?.error || "Erro desconhecido",
          }));
          addError(`❌ Erro ao sincronizar: ${p?.error || "Erro desconhecido"}`);
          break;

        case "REQUEST_PENDING_ORDERS":
          // Worker solicitou pedidos pendentes
          console.log("📨 [Main] Recebeu REQUEST_PENDING_ORDERS");
          try {
            const pending = await realOrderService.getPendingOrders();
            console.log("📦 [Main] Enviando pedidos pendentes:", pending.length);
            workerRef.current?.postMessage({
              type: "PENDING_ORDERS_RESPONSE",
              payload: pending,
            });
          } catch (error) {
            console.error("❌ [Main] Erro ao buscar pedidos pendentes:", error);
            workerRef.current?.postMessage({
              type: "PENDING_ORDERS_RESPONSE",
              payload: [],
            });
          }
          break;

        case "REQUEST_SYNC_EXECUTION":
          // Worker solicitou execução de sincronização
          console.log("📨 [Main] Recebeu REQUEST_SYNC_EXECUTION");
          try {
            const result = await dispatch(syncOrders()).unwrap();
            console.log("✅ [Main] Sincronização concluída, enviando resultado:", result);
            workerRef.current?.postMessage({
              type: "SYNC_EXECUTION_RESPONSE",
              payload: result,
            });
          } catch (error) {
            console.error("❌ [Main] Erro na sincronização:", error);
            const errorMessage = error instanceof Error ? error.message : "Erro na sincronização";
            workerRef.current?.postMessage({
              type: "SYNC_EXECUTION_RESPONSE",
              payload: {
                success: false,
                error: errorMessage,
              },
            });
          }
          break;

        default:
          console.warn("⚠️ Tipo de mensagem desconhecido do worker:", type);
      }
    },
    [dispatch, addSuccess, addError]
  );

  // Iniciar sincronização automática
  const startAutoSync = useCallback(() => {
    if (workerRef.current) {
      const interval = settings.updateEvery || 60;
      workerRef.current.postMessage({
        type: "START_SYNC",
        payload: { interval },
      });
    }
  }, [settings.updateEvery]);

  // Parar sincronização automática
  const stopAutoSync = useCallback(() => {
    if (workerRef.current) {
      workerRef.current.postMessage({ type: "STOP_SYNC" });
    }
  }, []);

  // Sincronizar agora (manual)
  const syncNow = useCallback(async () => {
    if (syncState.isSyncing) {
      addWarning("⏳ Sincronização já em andamento");
      return;
    }

    if (workerRef.current) {
      workerRef.current.postMessage({ type: "SYNC_NOW" });
    }
  }, [syncState.isSyncing, addWarning]);

  // Atualizar intervalo de sincronização
  const updateSyncInterval = useCallback((interval: number) => {
    if (workerRef.current) {
      workerRef.current.postMessage({
        type: "SET_INTERVAL",
        payload: { interval },
      });
    }
  }, []);

  // Iniciar sincronização automática ao montar (se configurado)
  useEffect(() => {
    const shouldAutoStart = localStorage.getItem("autoSyncEnabled") !== "false";
    if (shouldAutoStart && settings.updateEvery) {
      // Delay de 2 segundos após o mount para não sobrecarregar
      const timer = setTimeout(() => {
        startAutoSync();
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [settings.updateEvery, startAutoSync]);

  return {
    syncState,
    startAutoSync,
    stopAutoSync,
    syncNow,
    updateSyncInterval,
  };
};

export default useSyncWorker;
